import { SkeletonGradientBackground, TableComponent, CustomSelect } from '@/shared/ui/components';
import { DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import {
  zeroPad,
  dateToStringWithDailyTimeZone,
  gettextEventType,
  gettextSymptoms,
  GETTEXT_ALL_EVENT_TYPE,
} from '@/shared/lib';
import classNames from 'classnames';
import _ from 'lodash';
import { forwardRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import { t } from 'ttag';
import TooltipFormatter from '../tooltipFormatter';
import { useEventTableComponent, eventTypeList } from './useEventTableComponent';
import type { EventTableComponentProps } from './eventTableComponent.types';
import './styles.scss';

/**
 * Renders the ECG viewer's events table together with its event-type filter dropdown. Delegates state, row and dropdown handling to useEventTableComponent, builds the event id, trigger hour, type, symptoms and technician comment columns whose formatters fall back to skeletons while loading, and labels each filter option with its indicator count.
 *
 * @param props - Table inputs: event indicators, loaded flag, study start date, timezone, time offset and facility.
 * @param ref - Forwarded ref exposing the table's reload handle to the parent tab.
 * @returns The events table element with its filter dropdown.
 */
const EventTableComponent = forwardRef((props: EventTableComponentProps, ref) => {
  const { state, handleTableChange, handleOnRow, onClickDropdownItem } = useEventTableComponent(
    props,
    ref,
  );

  /**
   * Renders the technician comment cell of the ECG viewer events table. Once loaded it joins the comment fragments with spaces into a clamped tooltip, otherwise it shows a skeleton placeholder.
   *
   * @param cell - Array of technician comment fragments for the event.
   * @returns The tooltip element or a skeleton placeholder.
   */
  const technicianCommentsFormatter = (cell: any) => {
    const isLoadedData = state.isLoadedData && props.isLoadedDataProps;
    if (isLoadedData) {
      return <TooltipFormatter text={_.join(cell, ' ')} />;
    }
    return <Skeleton count={1} />;
  };

  /**
   * Renders the event id cell of the ECG viewer events table. Once loaded it zero-pads the friendly id, otherwise it shows a skeleton placeholder.
   *
   * @param cell - Friendly event id for the row.
   * @returns The padded id element or a skeleton placeholder.
   */
  const idFormatter = (cell: any) => {
    const isLoadedData = state.isLoadedData && props.isLoadedDataProps;
    if (isLoadedData) {
      return <span>{zeroPad(cell)}</span>;
    }
    return <Skeleton count={1} />;
  };

  /**
   * Renders the trigger hour cell of the events table in the study's local time. Once loaded it formats the ISO timestamp as HH:mm:ss through dateToStringWithDailyTimeZone using the study start date, timezone, time offset and facility, falling back to a bullet placeholder for empty values and a skeleton while loading.
   *
   * @param cell - ISO timestamp when the event was triggered.
   * @returns The formatted time element or a skeleton placeholder.
   */
  const triggerHourFormatter = (cell: any) => {
    const isLoadedData = state.isLoadedData && props.isLoadedDataProps;
    const { timezone, facility, studyTimeOffset } = props;
    if (isLoadedData) {
      return (
        <span>
          {cell
            ? dateToStringWithDailyTimeZone({
                startStudyDate: props.startStudyDate,
                isoDate: cell,
                timezone,
                studyTimeOffset,
                facility,
                format: 'HH:mm:ss',
              })
            : DOUBLE_HYPHEN_BULLET}
        </span>
      );
    }
    return <Skeleton count={1} />;
  };

  /**
   * Renders the event type cell of the ECG viewer events table. Shows the raw value once both the local and prop loaded flags are true, otherwise a single-line skeleton placeholder.
   *
   * @param cell - Event type value for the row.
   * @returns The cell element or a skeleton placeholder.
   */
  const eventTypeFormatter = (cell: any) => {
    const isLoadedData = state.isLoadedData && props.isLoadedDataProps;
    if (isLoadedData) {
      return <span>{cell}</span>;
    }
    return <Skeleton count={1} />;
  };

  /**
   * Renders the symptoms cell of the ECG viewer events table. Once loaded it maps 'None' to a not-specified label and otherwise translates the reason through gettextSymptoms, showing a skeleton while loading.
   *
   * @param cell - Symptom reason recorded for the event.
   * @returns The cell content or a skeleton placeholder.
   */
  const reasonFormatter = (cell: any) => {
    const isLoadedData = state.isLoadedData && props.isLoadedDataProps;
    if (isLoadedData) {
      return cell === 'None' ? t`Not specified` : gettextSymptoms(cell) || '';
    }
    return <Skeleton count={1} />;
  };

  /**
   * Defines the events table columns: event id, trigger hour, event type, symptoms and technician comment. Each entry pairs a data index and translated title with its matching formatter and a fixed pixel width.
   *
   * @returns Column definitions for the events table.
   */
  const generateColumns = () => [
    {
      dataIndex: 'friendlyId',
      title: t`Event ID`,
      render: idFormatter,
      width: 120,
    },
    {
      dataIndex: 'start',
      title: t`Trigger hour`,
      render: triggerHourFormatter,
      width: 120,
    },
    {
      dataIndex: 'type',
      title: t`Event type`,
      render: eventTypeFormatter,
      width: 120,
    },
    {
      dataIndex: 'reason',
      title: t`Symptoms`,
      render: reasonFormatter,
      width: 200,
    },
    {
      dataIndex: 'comments',
      title: t`Technician comment`,
      render: technicianCommentsFormatter,
      width: 385,
    },
  ];

  /**
   * Counts the event indicators matching one event type for the filter dropdown labels. Uses the full indicator list for the all-types option and a filtered count otherwise, falling back to zero.
   *
   * @param option - Selected event type, or the all-event-types option.
   * @returns Number of indicators matching the option.
   */
  const getEventCount = (option: any) =>
    (option === GETTEXT_ALL_EVENT_TYPE
      ? props.eventIndicators.length
      : _.filter(props.eventIndicators, (x) => option === x.type)?.length) || 0;

  return (
    <div
      className={classNames(
        'event-table-component',
        !(state.isLoadedData && props.isLoadedDataProps) ? 'relative' : '',
      )}
    >
      <SkeletonGradientBackground isShow={!(state.isLoadedData && props.isLoadedDataProps)} />

      <div className="options">
        <div className="content">
          <div>
            {t`Events`} <span>(Click to present on ECG viewer)</span>
          </div>
        </div>
        <div className="option">
          <CustomSelect
            options={eventTypeList.map((option) => ({
              label: gettextEventType(option),
              value: option,
              title: `${option} (${getEventCount(option)})`,
            }))}
            style={{ width: 180 }}
            showSearch
            allowClear={false}
            value={state.selectedEventType}
            size="middle"
            onChange={(name, value) => {
              onClickDropdownItem(value);
            }}
            id="ecg-viewer-event-type"
            name="eventType"
            aria-label={t`Event type`}
          />
        </div>
      </div>

      <TableComponent
        isLoading={!(state.isLoadedData || props.isLoadedDataProps)}
        data={state.events}
        columns={generateColumns()}
        onRow={handleOnRow}
        isEndOfData={state.isEndOfData}
        selectedRowKey={state.selectedRow}
        currentPage={state.page}
        pageSize={state.sizePerPage}
        handleTableChange={handleTableChange}
        paginationId="ecg-pagination-size-changer"
      />
    </div>
  );
});

(EventTableComponent as any).defaultProps = {
  studyFid: undefined,
  timezone: undefined,
  selectedDate: null,
  facility: {},
};

export default EventTableComponent;
